import { useEffect, useState } from "react";
import { useRouteLoaderData } from "react-router-dom";

import { type PrivateLoaderData } from "components/root/router";
import { isError } from "data/ss-error";
import { useKeystone } from "data/ss-store";
import {
  type ClaimList,
  useClaimsPageDataQuery,
} from "gql/__generated__/hooks";
import { type KeystoneApiClient, useFlags } from "utils";

const POLL_INTERVAL = 2000;

// This hook can deliver claims data from graphql or keystone api, depending on the
// retireProxy feature flag
export function useClaimsData(policyId: string) {
  const { retireProxy } = useFlags();
  const loaderData = useRouteLoaderData("private") as PrivateLoaderData;
  const [restLoading, setRestLoading] = useState(false);
  const [restClaimsList, setRestClaimsList] = useState<ClaimList>();
  const keystone = useKeystone();

  const { error, loading, data, startPolling, stopPolling } =
    useClaimsPageDataQuery({
      variables: {
        policyID: policyId,
      },
    });

  const carrierInfo = retireProxy ? loaderData?.carrier : data?.userCarrierInfo;

  const info = data?.userClaimsList?.ClaimsTrackerInfo;
  const pollGql =
    info?.length &&
    info?.every((claim) => claim.CarrierClaim?.Number !== "PENDING");

  useEffect(() => {
    // Retire proxy data handling
    if (retireProxy) {
      (async () => {
        setRestLoading(true);
        const list = await loaderData?.claimsList;
        setRestClaimsList(list);
        setRestLoading(false);
        if (checkPending(list)) {
          pollClaimsListWhilePending(policyId, setRestClaimsList, keystone);
        }
      })();
      return;
    }

    // GraphQL data handling
    if (!error && pollGql) {
      startPolling(POLL_INTERVAL);
    } else {
      stopPolling();
    }
  }, [
    retireProxy,
    pollGql,
    startPolling,
    stopPolling,
    policyId,
    loaderData?.claimsConstants,
    loaderData?.claimsList,
    keystone,
    error,
  ]);

  // GraphQL data
  if (!retireProxy) {
    return {
      error,
      loading,
      claimsList: data?.userClaimsList,
      carrierInfo,
    };
  }

  // Retired proxy data
  return {
    loading: restLoading,
    claimsList: restClaimsList,
    carrierInfo,
  };
}

// Returns true if ClaimsTrackerInfo has been fetched and at least one entry is "PENDING"
function checkPending(list?: ClaimList) {
  return (
    list?.ClaimsTrackerInfo?.length &&
    list?.ClaimsTrackerInfo?.some(
      (claim) => claim.CarrierClaim?.Number === "PENDING"
    )
  );
}

async function pollClaimsListWhilePending(
  policyId: string,
  setResult: (res: ClaimList | undefined) => void,
  keystone: KeystoneApiClient
) {
  const res = await keystone.getClaimsList(policyId);
  if (isError(res)) {
    console.error("Polling claims list", res.message);
    return;
  }
  setResult(res);
  if (checkPending(res)) {
    setTimeout(
      () => pollClaimsListWhilePending(policyId, setResult, keystone),
      POLL_INTERVAL
    );
  }
}
